import { useEffect, useState, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import type { TeamRole } from "@/lib/permissions";

interface AgencyInfo {
  id: string;
  name: string;
  segment: string | null;
  plan: string;
  owner_id: string;
  seat_limits: { owner: number; manager: number; staff: number };
  current_seats: { owner: number; manager: number; staff: number };
}

interface TeamRoleState {
  teamRole: TeamRole | null;
  agencyId: string | null;
  agency: AgencyInfo | null;
  loading: boolean;
  refresh: () => Promise<void>;
}

export function useTeamRole(): TeamRoleState {
  const { user, loading: authLoading } = useAuth();
  const [teamRole, setTeamRole] = useState<TeamRole | null>(null);
  const [agencyId, setAgencyId] = useState<string | null>(null);
  const [agency, setAgency] = useState<AgencyInfo | null>(null);
  const [loading, setLoading] = useState(true);
  // Tracks which user the current teamRole/agency state belongs to, so a
  // render right after `user` changes (before the effect below has re-run)
  // can be recognized as still-loading instead of exposing stale data for
  // the previous user — mirrors the fix in useProfile.
  const loadedForUserId = useRef<string | null>(null);

  // Depend on the user's id, not the whole `user` object — see the matching
  // comment in useProfile.ts. Supabase issues a new session/user object on
  // every token refresh (e.g. right after the tab regains focus) even though
  // it's the same signed-in user; depending on the object reference caused a
  // needless refetch-with-loading-spinner cycle on every refresh.
  const userId = user?.id;
  const fetchData = useCallback(async () => {
    if (!userId) {
      loadedForUserId.current = null;
      setTeamRole(null);
      setAgencyId(null);
      setAgency(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data: prof } = await supabase
        .from("profiles")
        .select("team_role, agency_id")
        .eq("id", userId)
        .maybeSingle();

      const role = (prof?.team_role as TeamRole) ?? "owner";
      const aid = prof?.agency_id ?? null;
      setTeamRole(role);
      setAgencyId(aid);

      if (aid) {
        const { data: agencyRow } = await supabase
          .from("agencies")
          .select("id, name, segment, plan, owner_id, seat_limits, current_seats")
          .eq("id", aid)
          .maybeSingle();
        if (agencyRow) {
          setAgency({
            ...agencyRow,
            seat_limits: agencyRow.seat_limits as any,
            current_seats: agencyRow.current_seats as any,
          });
        }
      }
    } catch (err) {
      console.warn("useTeamRole fetch failed", err);
    } finally {
      loadedForUserId.current = userId;
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const effectiveLoading = authLoading || loading || (!!user && loadedForUserId.current !== user.id);

  return { teamRole, agencyId, agency, loading: effectiveLoading, refresh: fetchData };
}
